import { Injectable, Injector, NgZone, OnDestroy } from "@angular/core";
import { Platform } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { Console } from "console";
import moment from "moment";
import {
  IMqttMessage,
  IMqttServiceOptions,
  IOnErrorEvent,
  MqttConnectionState,
  MqttService,
} from "ngx-mqtt";
import { firstValueFrom, from, Observable, Subscription, throwError, zip } from "rxjs";
import { delay, filter, map, take, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AppReserved } from "src/model/class/general classes/app-reserved";
import { CurrentSessionData } from "src/model/class/general classes/current-session-data";
import { Plant } from "src/model/class/general classes/plant";
import { AddressDefaultMethods } from "src/components/shared-components/enum/enumAddress";
import { BIGDeviceOperationMode } from "src/components/shared-components/enum/enumDevice";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import {
  ApplicationTheme,
  ToastConnectionCodes,
} from "src/components/shared-components/enum/enumMisc";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { MQTTMessageACK } from "src/model/interface/mqtt-message-ack";
import { MQTTMessageAddress } from "src/model/interface/mqtt-message-address";
import { translationForceReloadAction } from "src/state/actions/translation.actions";
import { currentUserData } from "src/state/selectors/authentication.selectors";
import { selectedPlant } from "src/state/selectors/plant.selectors";
import { AlertService } from "./alert.service";
import { EventService } from "./event.service";
import { LoggerService } from "./logger.service";
import { ThemeService } from "./theme.service";
import { ToastService } from "./toast.service";

@Injectable({
  providedIn: "root",
})

/** The connectOnCreate options is used to do an automatic resubscribtion of the topic previously used
 * when the mqtt client is disconnected and the reconnected to a broker
 * BUG ON THE MQTT-NGX LIBRARY: The parameter, also when is setted in this way, is not bounded to
 * the correspondant parameter in the library, so is not considered. For this reason we have to add in
 * file node_modules\ngx-mqtt\src\mqtt.service.js (row 114) the following two rows:
 *      if (opts && opts.connectOnCreate)
 *        this.options.connectOnCreate = opts.connectOnCreate;
 */
export class MqttClientService implements OnDestroy {
  private _logContext: string = "Mqtt-client.service";

  //#region Subcriptions to "_store.select(...)" Observables
  private _selectCurrentUserSub: Subscription = null;
  private _isDigitalPlaceSubscribtion: Subscription = null;
  //#endregion

  private _currentMqttConnectionStatus: MqttConnectionState =
    MqttConnectionState.CLOSED;
  private _messageRefId = 1;
  private _isUserAuthenticated: boolean = false;
  private _currentUsername: string = "";
  private _currentPassword: string = "";
  private _currentHost: string = ";";
  private _currentPlant: Plant = null;

  //#region  Subscription
  private _subscriptionLogout: Subscription;
  private _subscriptionLogoutCompleted: Subscription;
  private _subscriptionServerRootChanged: Subscription;
  private _subscriptionLogin: Subscription;
  private _subscriptionOnline: Subscription;
  private _subscriptionOffline: Subscription;
  private _selectCurrentPlantSub: Subscription;
  //#endregion

  private _connectionMqttOptionTEST: any = null;

  private _disconnectionNumber: number = 0;

  /** The logout asked to disconnect, which is done on "OnLogoutCompleted" (see {@link disconnectForLogout}) */
  private _isLogoutDisconnectionPending: boolean = false;

  /**
   * Return the actual status of the MQTT connection
   */
  get Status(): MqttConnectionState {
    return this._currentMqttConnectionStatus;
  }

  /**
   * Handle MQTT Client ID
   */
  get clientID(): string {
    return this.mqttService.clientId;
  }

  constructor(
    protected injector: Injector,
    protected mqttService: MqttService,
    protected eventService: EventService,
    protected platform: Platform,
    protected alertService: AlertService,
    protected toastService: ToastService,
    private _ngZone: NgZone,
    private _loggerService: LoggerService,
    private _store: Store,
    private _themeService: ThemeService
  ) {
    this.registerPlatformEvent();
    this.handleMqttNaviteEvent();
    this.subscribeEvent();
    this.checkConnectionState();

    /** GF: used to keep the current status */
    this.mqttService.state.subscribe((currentStatus: MqttConnectionState) => {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        "mqttService",
        "state",
        "Status mqtt state connection from service: " + currentStatus
      );
      this._currentMqttConnectionStatus = currentStatus;

      if (currentStatus == MqttConnectionState.CLOSED) {
        this._disconnectionNumber = this._disconnectionNumber + 1;
        this._loggerService.log(
          LogLevel.LOG_WARNING,
          this._logContext,
          this._logContext,
          "constructor()",
          "DISCONNECTION NUMBER: " + this._disconnectionNumber
        );
      }
    });

    this.subscribeCurrentPlant();

    /** Checking if current user is authenticated */
    this._selectCurrentUserSub = this._store
      .select(currentUserData)
      .pipe(
        filter((currentUserData: GenericUserData) => currentUserData !== null),
        map((currentUserData: GenericUserData) => !!currentUserData.currentUser)
      )
      .subscribe((isAuthenticated: boolean) => {
        this._isUserAuthenticated = isAuthenticated;

        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          "constructor()",
          "_isUserAuthenticated: " + this._isUserAuthenticated
        );

        //When user reload page on digital place platform,
        //whene user store is loaded and it has been authenticated,
        //it performs mqtt connection status check
        if (this._isUserAuthenticated) {
          this.checkConnectionState();
        }
      });
  }

  ngOnDestroy(): void {
    this._subscriptionLogout?.unsubscribe();
    this._subscriptionLogoutCompleted?.unsubscribe();
    this._subscriptionServerRootChanged?.unsubscribe();
    this._subscriptionLogin?.unsubscribe();
    this._subscriptionOffline?.unsubscribe();
    this._subscriptionOnline?.unsubscribe();
    this.unsubscribeStoreSelect();

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      "eventService",
      "OnServerRootChanged",
      "DISCONNECT from mqtt-server for ngOnDestroy"
    );

    this.disconnect();
  }

  private subscribeCurrentPlant(): void {
    this._selectCurrentPlantSub = this._store
      .select(selectedPlant)
      .pipe
      //filter((currentPlant) => currentPlant !== null),

      /* Only the first (not-null) emission is considered, since the "client-settings" are in this page ONLY READ when entering the page */
      /*
        take(1),
  */
      ()
      .subscribe((currentPlant: Plant) => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          "subscribeCurrentPlant()",
          "New plant selection: " + JSON.stringify(currentPlant)
        );

        this._currentPlant = new Plant(currentPlant);
      });
  }

  /**
   * Show/clear the toast that says that the mqtt is disconnected or reconnected.
   * In both case, end use see same toast beacuse he doesn't metter current connection state (disconnected or connecting).
   * When these kind of toast are shown in background, reconnection process occuring
   */
  private async handleToastConnection() {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "handleToastConnection()",
      "START - this._mqttRoot: " +
        environment.httpServerRoot +
        " || _isUserAuthenticated: " +
        this._isUserAuthenticated
    );

    if (environment.httpServerRoot !== "" && this._isUserAuthenticated) {
      switch (this._currentMqttConnectionStatus) {
        case MqttConnectionState.CLOSED:
          await this.openMqttProblemToast();
          break;
        case MqttConnectionState.CONNECTING:
          await this.openMqttProblemToast();
          break;
        /* The "default" value is normally associated to the "CONNECTED" status of mqtt-client */
        default:
          await this.closeAllToast();
          break;
      }
    } else if (!this._isUserAuthenticated) {
      await this.closeAllToast();
    }
  }

  /**
   * Connect to Digital Place WS with specific username and password
   *
   * @param username Username who perform authentication
   * @param password Password whi
   */
  private connectDigitalPlace(username: string, password: string): boolean {
    let isConnected: boolean = false;

    try {
      const connectionMqttOption: IMqttServiceOptions = {
        hostname: environment.wsHostName,
        username: username,
        password: password,
        port: Number(environment.wsPort),
        connectOnCreate: true,
        path: "/mqtt",
        protocol: "wss",
        //protocol: 'wss',
        //Possibile soluzione per compilazione dinamica
        //protocol: (environment.mqttProtocol === “wss”) ? “wss” : “ws”,
        connectTimeout: 10000,
      };

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "connectDigitalPlace()",
        "EXECUTING CONNECTION WITH OPTION: " +
          "HOSTname: " +
          connectionMqttOption?.hostname +
          " | Username: " +
          connectionMqttOption?.username +
          " | port: " +
          connectionMqttOption?.port +
          " | protocol: " +
          connectionMqttOption?.protocol +
          " | connectTimeout: " +
          connectionMqttOption?.connectTimeout
      );
      this.mqttService.connect(connectionMqttOption);

      isConnected = true;
    } catch (exception) {
      isConnected = false;
    }

    return isConnected;
  }

  /**
   * Read the informations about the connection to the broker and try to connect
   */
  public async connect(): Promise<boolean> {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "connect()",
      "USE SERFVER-ROOT: " + environment.httpServerRoot
    );

    if (environment.httpServerRoot === "") {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "connect()",
        "Server root undefined! Impossible to connect."
      );

      return false;
    }

    return await this.performDigitalPlaceMqttConnection();
  }

  /**
   * Close the connection left open by the logout, once. Called on "OnLogoutCompleted", or by the next login
   * if it comes first: whichever is later finds nothing pending and does nothing.
   */
  private disconnectForLogout(): void {
    if (!this._isLogoutDisconnectionPending) {
      return;
    }

    this._isLogoutDisconnectionPending = false;

    this.disconnect();

    this.eventService.OnComeBackOfflineMqtt.next(null);
  }

  /**
   * Handle native disconnection library
   */
  private disconnect(): void {
    try {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "disconnect()",
        "DISCONNECTING FROM MQTT-SERVER"
      );
      this.mqttService.disconnect(true);
    } catch (error) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "disconnect()",
        "Unable to disconnect from MQTT-SERVER: " + error?.message
      );
    }
  }

  /**
   * When subscribe an address, if the address is already subscribed
   * the subscribtion (.observe) returns the observable object used by the mqttService;
   * otherwise the subscribtion is actually done
   * The subscription is done only if the mqtt connection is ok
   *
   * @param addressToSubscribe Address to subscribe
   */
  public async subscribeTopic(
    addressToSubscribe: string
  ): Promise<Observable<IMqttMessage>> | null {
    try {
      if (this.checkAddressToSubscribe(addressToSubscribe)) {
        let observableAddress: Observable<IMqttMessage> = null;

        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          "subscribeTopic()",
          "START: " + addressToSubscribe
        );

        await this.mqttService.state
          .pipe(
            filter(
              (state: MqttConnectionState) =>
                state == MqttConnectionState.CONNECTED
            ),
            take(1)
          )
          .toPromise();

        observableAddress = this.mqttService.observe(addressToSubscribe);

        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          "subscribeTopic()",
          "END Subscription to: " + addressToSubscribe
        );

        return observableAddress;
      } else {
        return null;
      }
    } catch (exception) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "subscribeTopic()",
        "ERROR:  " + exception.message
      );
      throw new Error("subscribeTopic - " + exception);
    }
  }

  /**
   * Check address to subscribe.
   * Address must be:
   * - must be contain and underscore
   *
   * @param address
   */
  protected checkAddressToSubscribe(address: string): boolean {
    return address !== "_" && address.length > 0;
  }

  /**
   * Used to make a read on a specific address
   * @param address Address to read
   */
  public publishAddressRead(address: string): void {
    const message = {
      Message: {
        Ref: this._messageRefId,
        Action: AddressDefaultMethods.READ,
      },
    };

    this.mqttService.unsafePublish(address, JSON.stringify(message), {
      qos: 1,
      retain: false,
    });
    this._messageRefId++;
  }

  /**
   * Used to write an address on the mqtt
   * @param address address to write
   * @param value value of the address: ONLY STRING value
   * @param action default action is WRITE, if you need to send a refresh you need to write RESFRESH
   */
  public publishAddress(
    address: string,
    value: string,
    action: string = AddressDefaultMethods.WRITE
  ): void {
    value = value.replace(/'/g, "\\'");

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      "mqttService",
      "publishAddress()",
      "Publishing on address: " + address + " || value: " + value
    );

    const message: MQTTMessageAddress = {
      Message: {
        Ref: this._messageRefId,
        TimeStamp: moment().format("YYYYMMDDHHmmss"),
        Action: action,
        Value: value,
        ClientID: this.mqttService.clientId,
      },
    };

    this.mqttService.unsafePublish(address, JSON.stringify(message), {
      qos: 1,
      retain: false,
    });

    this._messageRefId++;
  }

  /**
   * Send generic ACK to web socket by message ref ID
   *
   * @param messageRef Message ref id
   * @param instanceGUID Identify gateway instance guid to localize ACK
   */
  public sendACK(messageRef: string, instanceGUID: string) {
    const message: MQTTMessageACK = {
      Message: {
        Ref: messageRef,
        Result: "ACK",
        Timestamp: moment().format("YYYYMMDDHHmmss"),
        ErroCode: "",
        ErrorDescription: "",
        Parameters: "",
        InstanceGUID: instanceGUID,
      },
    };

    this.mqttService.unsafePublish(
      AppReserved.ADDRESS_ACK_NACK,
      JSON.stringify(message),
      {
        qos: 1,
        retain: false,
      }
    );

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      "mqttService",
      "sendACK",
      "Ref: " + messageRef + " || instanceGUID: " + instanceGUID
    );

    this._messageRefId++;
  }

  /**
   * Used to write an address on the mqtt
   * @param address address to write
   * @param value value of the address
   */
  public publishAddressWrite(
    address: string,
    stateAddressInstanceGUID: string,
    stateAddressAddress: string,
    stateAddressValue: string,
    evaluate: boolean,
    value: string
  ): void {
    const message = {
      Message: {
        Ref: this._messageRefId,
        Action: AddressDefaultMethods.WRITE,
        Value: value,
        StateAddress_InstanceGUID: stateAddressInstanceGUID,
        StateAddress_Address: stateAddressAddress,
        StateAddress_Value: stateAddressValue,
        Evaluate: evaluate,
        ClientID: this.mqttService.clientId,
      },
    };

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      "mqttService",
      "publishAddressWrite()",
      "Publishing on address: " +
        address +
        " || message: " +
        JSON.stringify(message)
    );

    this.mqttService.unsafePublish(address, JSON.stringify(message), {
      qos: 1,
      retain: false,
    });
    this._messageRefId++;
  }

  /**
   * Parse Mqtt message to string array
   *
   * @param mqttMessage Mqtt original message
   * @returns
   */
  public parseMessage(mqttMessage: IMqttMessage): Array<string> {
    const message = JSON.parse(
      mqttMessage.payload.toString()
    ).Message.Value.split("|");

    message.push(JSON.parse(mqttMessage.payload.toString()).Message.ClientID);

    return message;
  }

  /**
   * Register event from platform event
   */
  private registerPlatformEvent(): void {
    this.platform.resume.subscribe(async (e) => {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        "platform",
        "resume - REACT",
        "START "
      );

      await this.checkConnectionState();
    });
  }

  /**
   * Check the actual connection status and if needed try to reconnect
   */
  private async checkConnectionState(): Promise<void> {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "checkConnectionState()",
      "START. Current this._mqttRoot: " +
        environment.httpServerRoot +
        " | isUserAuthenticated: " +
        this._isUserAuthenticated
    );
    if (environment.httpServerRoot !== "" && this._isUserAuthenticated) {
      switch (this._currentMqttConnectionStatus) {
        case MqttConnectionState.CONNECTED:
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            this._logContext,
            "checkConnectionState()",
            "CONNECTED"
          );
          await this.closeAllToast();
          break;
        case MqttConnectionState.CONNECTING:
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            this._logContext,
            "checkConnectionState()",
            "CONNECTING"
          );
          await this.handleToastConnection();
          break;
        case MqttConnectionState.CLOSED:
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            this._logContext,
            "checkConnectionState()",
            "CLOSED"
          );
          await this.handleToastConnection();

          await this.connect();
          break;
        default:
          break;
      }
    }
  }

  /**
   * Handle native library event //
   */
  private handleMqttNaviteEvent(): void {
    this.mqttService.onConnect.subscribe(async (connEvent: IMqttMessage) => {
      this._ngZone.run(async () => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          "handleMqttNaviteEvent()",
          "onConnect - Connection with the WebSocket OK - clientID: " +
            this.mqttService.clientId
        );
        this.eventService.OnComeBackOnlineMqtt.next(null);
        this._store.dispatch(translationForceReloadAction());
        await this.handleToastConnection();
      });
    });

    this.mqttService.onReconnect.subscribe(async (end) => {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "handleMqttNaviteEvent()",
        "onReconnect - reconnecting to the MQTT-SERVER"
      );
      //alert("Reconnect");
      // await this.handleToastConnection(); //
    });

    /**
     * Called when the broker inside the WebSocket is offline
     */
    this.mqttService.onOffline.subscribe(async (offline) => {
      this._loggerService.log(
        LogLevel.LOG_WARNING,
        this._logContext,
        this._logContext,
        "handleMqttNaviteEvent()",
        "onOffline - WEBSOCKET UNREACHABLE"
      );

      //this.eventService.OnComeBackOfflineMqtt.next(); AB: già presente nell'onOffline

      await this.handleToastConnection();
    });

    /** GF: when the connection is closed show the toast of unreachable server */
    this.mqttService.onClose.subscribe(async () => {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "handleMqttNaviteEvent()",
        "onClose - connection with the MQTT-SERVER closed"
      );

      this.eventService.OnComeBackOfflineMqtt.next(null);

      await this.handleToastConnection();
    });

    this.mqttService.onEnd.subscribe(async (end) => {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "handleMqttNaviteEvent()",
        "onEnd - MQTT client ended"
      );

      await this.handleToastConnection();
    });

    this.mqttService.onError.subscribe((error: IOnErrorEvent) => {
      // Expected on logout / server change: disconnect() runs first, then the services drop their
      // topic subscriptions and mqtt.js refuses the unsubscribe of a client that is already
      // disconnecting. Harmless: the broker drops those subscriptions with the connection.
      if (error?.message === "client disconnecting") {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "ngx-mqtt",
          "onError",
          "Unsubscribe ignored, client is disconnecting"
        );
        return;
      }

      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        "ngx-mqtt",
        "onError",
        "ERROR IN MQTT-SERVER CONNECTION: " + error.message
      );
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "handleMqttNaviteEvent()",
        "onError - option used in connection: " +
          JSON.stringify(this._connectionMqttOptionTEST)
      );
    });
  }

  /**
   * Subscrbe event from event service
   */
  private subscribeEvent(): void {
    this._subscriptionOnline = this.eventService.OnComeBackOnline.subscribe(
      () => {}
    );

    this._subscriptionOffline =
      this.eventService.OnRequestDisconnectMqtt.subscribe(() => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "eventService",
          "OnRequestDisconnectMqtt",
          "DISCONNECT from mqtt-server for OnRequestDisconnectMqtt"
        );

        this.disconnect();
      });

    /** GF: when the logout is done the mqtt must be disconnected and the list of previously subscribed topic
     * must be cleaned from all the specific addresses (only the reserved are saved)
     * (otherwise when I change a plant and the mqttClient is connected again to the broker it tries
     * to resubscribe the old topics);
     */
    this._subscriptionLogout = this.eventService.OnMakeLogout.subscribe(() => {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        "eventService",
        "OnMakeLogout",
        "DISCONNECT from mqtt-server for OnMakeLogout"
      );

      this._disconnectionNumber = 0;

      /* NOTE: the disconnection waits for "OnLogoutCompleted". The services drop their topic subscriptions in
       * their own OnMakeLogout handlers and the pages of the reserved area in ngOnDestroy(), after the transition
       * to the splash page: unsubscribing on a client that is already ending makes mqtt.js emit the
       * "client disconnecting" error. */
      this._isLogoutDisconnectionPending = true;

      this._isUserAuthenticated = false;
      const observableKeys = Object.keys(this.mqttService.observables);

      for (const key of observableKeys) {
        if (!key.includes("RESERVED")) {
          delete observableKeys[key];
        }
      }

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        "_eventService",
        "OnMakeLogout",
        "NARTIVE ARRAY ."
      );
      for (const entry of Array.from(
        new Map(Object.entries(this.mqttService.observables)).entries()
      )) {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "_eventService",
          "OnMakeLogout",
          "NARTIVE ARRAY  Entry:  " + entry[0]
        );
      }
    });

    this._subscriptionLogoutCompleted =
      this.eventService.OnLogoutCompleted.subscribe(() => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "eventService",
          "OnLogoutCompleted",
          "DISCONNECT from mqtt-server for OnLogoutCompleted"
        );

        this.disconnectForLogout();
      });

    /** GF: when the login is done we have all the informations to do the connection */
    this._subscriptionLogin = this.eventService.OnLoginPlantDid.subscribe(
      async () => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "eventService",
          "OnLoginPlantDid - REACT",
          "START "
        );

        /* The logout left the connection of the previous plant open and "OnLogoutCompleted" has not come yet:
         * checkConnectionState() would find it CONNECTED and keep it, so it is closed here first. */
        if (this._isLogoutDisconnectionPending) {
          this._loggerService.log(
            LogLevel.LOG_WARNING,
            this._logContext,
            "eventService",
            "OnLoginPlantDid - REACT",
            "LOGOUT DISCONNECTION STILL PENDING. DISCONNECTING BEFORE THE NEW CONNECTION."
          );

          this.disconnectForLogout();

          await firstValueFrom(
            this.mqttService.state.pipe(
              filter(
                (state: MqttConnectionState) =>
                  state !== MqttConnectionState.CONNECTED
              )
            )
          );
        }

        this._isUserAuthenticated = true;

        this._disconnectionNumber = 0;

        await this.checkConnectionState();
      }
    );

    /** AB: when OnServerRootChanged, checkConnection status of MQTT   */
    this._subscriptionServerRootChanged =
      this.eventService.OnServerRootChanged.pipe(
        tap(() => {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "eventService",
            "OnServerRootChanged",
            "DISCONNECT from mqtt-server for serverRootChanged"
          );

          // Deferred for the same reason as in OnMakeLogout (reconnection still follows the delay below)
          setTimeout(() => this.disconnect());
        }),
        delay(1000)
      ).subscribe(async () => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "eventService",
          "OnServerRootChanged",
          "server root: " + environment.httpServerRoot
        ); //

        //NOTE: This is necessary in order to force the service to re-connect mqtt-Client to the new this._mqttRoot
        //this.disconnect();

        await this.checkConnectionState();
      });
  }

  /**
   * Open toast related to mqtt connection problems
   */
  private async openMqttProblemToast(): Promise<void> {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "openMqttProblemToast()",
      "START. operativeMode: " + this._currentPlant?.operativeMode
    );

    if (
      this._currentPlant?.operativeMode !==
      BIGDeviceOperationMode.ACCESS_CONTROL_MODE
    ) {
      /*await this.toastService.showToastConnectionIssues(
        ToastConnectionCodes.MQTT_CONNECTION_LOST
      );*/
    }
  }

  /**
   * Close all mqtt activate toast
   */
  private async closeAllToast(): Promise<void> {
    if (
      this._currentPlant?.operativeMode !==
      BIGDeviceOperationMode.ACCESS_CONTROL_MODE
    ) {
      await this.toastService.closeToastConnectionIssues(
        ToastConnectionCodes.MQTT_CONNECTION_REGAIN
      );
    }
  }

  /**
   * Perform digital place connection procedure
   *
   * @returns
   */
  private async performDigitalPlaceMqttConnection(): Promise<any> {
    return this._store
      .select(currentUserData)
      .pipe(
        take(1),
        map((currentUserData: GenericUserData) => currentUserData?.currentUser),
        map((currentUserData: CurrentSessionData) => {
          let isConnected: boolean = false;

          try {
            isConnected = this.connectDigitalPlace(
              currentUserData.username,
              currentUserData.password
            );

            isConnected = true;
          } catch (exception) {}

          return isConnected;
        }),
        tap(() => from(this.closeAllToast()))
      )
      .toPromise();
  }

  /**
   * Perform mqtt connection for BIGStudio
   *
   * @returns
   */
  private async peformBIGStudioMqttConnection(): Promise<any> {
    let isConnected: boolean = false;
    let mqttHost: string = "";
    let mqttPort: number = 0;

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "peformMqttConnection()",
      "USE SERFVER-ROOT: " + environment.httpServerRoot
    );

    return zip(
      this._store.select(currentUserData),
      this._store.select(selectedPlant)
    )
      .pipe(
        take(1),
        map((resultZip: Array<any>) => {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            this._logContext,
            "peformMqttConnection()",
            "START 1"
          );

          const currentUserObject: CurrentSessionData = (
            resultZip[0] as GenericUserData
          )?.currentUser; //current user

          let connectionMqttOption = null;

          if (currentUserObject && this._currentPlant) {
            this._currentUsername = currentUserObject.username;
            this._currentPassword = currentUserObject.password;
            mqttHost = environment.httpServerRoot
              .replace("http://", "")
              .replace("https://", "");

            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              this._logContext,
              "peformMqttConnection()",
              "PASSWORD: " + this._currentPassword + ", MQTTHOST: " + mqttHost
            );

            if (mqttHost.includes(this._currentPlant.LANAddress)) {
              this._currentHost = mqttHost.replace(
                ":" + this._currentPlant.LanPort,
                ""
              );
              mqttPort = Number(this._currentPlant.LANMqttPort);
            } else {
              this._currentHost = mqttHost.replace(
                ":" + this._currentPlant.WanPort,
                ""
              );
              mqttPort = Number(this._currentPlant.WANMqttPort);
            }

            connectionMqttOption = {
              hostname: this._currentHost,
              username: this._currentUsername,
              password: this._currentPassword,
              port: mqttPort,
              connectOnCreate: true,
              path: "/mqtt",
              connectTimeout: 10000,
            };

            this._connectionMqttOptionTEST = connectionMqttOption;
          } else {
            return throwError(new Error());
          }

          return connectionMqttOption;
        }),
        map((connectionMqttOption) => {
          let isConnected: boolean = false;

          try {
            this.mqttService.connect(connectionMqttOption);

            isConnected = true;
          } catch (exception) {
            this._loggerService.log(
              LogLevel.LOG_ERROR,
              this._logContext,
              this._logContext,
              "peformBIGStudioMqttConnection()",
              "ERROR IN native Connect method: " + exception?.message
            );
          }

          return isConnected;
        }),
        tap(() => from(this.closeAllToast()))
      )
      .toPromise();
  }

  //#region Unsubscribe from "_store.selec(...)" Observable
  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    if (this._selectCurrentUserSub) {
      this._selectCurrentUserSub.unsubscribe();
    }

    this._selectCurrentPlantSub?.unsubscribe();
  }
  //#endregion
}
